// BACKEND CONTRACT — referrals module
//
// Endpoints:
//   POST /referrals/codes  → creates or retrieves the user's code (idempotent)
//   GET  /referrals/me     → returns all referral data in one call
//
// Status transitions (backend must enforce):
//   registered → converted/converted_no_reward  when the referred user completes onboarding
//   converted  → reward_eligible                manually by ops after validating the transfer
//   any        → cancelled                      manually by ops (must be excluded from GET /referrals/me)
//
// HTTP errors:
//   401 → missing or invalid token
//   403 → user not eligible (e.g. no active plan); frontend currently shows generic error
//   404 → no data yet (e.g. GET /referrals/me before any code exists)
//
// Business rules:
//   - Any authenticated user can generate a referral code
//   - Rewards only apply to transferred plans — new plans produce converted_no_reward
//   - reward_snapshot is frozen at code creation time; changing the active reward
//     does NOT update existing codes, only new ones
//   - cancelled attributions must not appear in GET /referrals/me (frontend also filters defensively)

// Attribution lifecycle statuses:
//   registered          → signed up but hasn't completed transfer or opened a new plan yet
//   converted           → completed transfer (eligible for reward, not yet processed)
//   reward_eligible     → transfer validated, reward ready to apply
//   converted_no_reward → opened a new plan (not a transfer) — no reward generated
//   cancelled           → process cancelled — filtered out in UI, never shown
//
// Use REFERRAL_ATTRIBUTION_STATUS (below) for comparisons instead of magic strings.
export const REFERRAL_ATTRIBUTION_STATUS = {
	Registered: 'registered',
	Converted: 'converted',
	RewardEligible: 'reward_eligible',
	ConvertedNoReward: 'converted_no_reward',
	Cancelled: 'cancelled'
} as const;
export type ReferralAttributionStatus =
	(typeof REFERRAL_ATTRIBUTION_STATUS)[keyof typeof REFERRAL_ATTRIBUTION_STATUS];

// Distinguishes transfer vs new plan. In V1 both show as "Proceso iniciado",
// but the field is ready to handle them differently in the future.
export const REFERRAL_PLAN_TYPE = {
	Transfer: 'transfer',
	New: 'new',
	Unknown: 'unknown'
} as const;
export type ReferralPlanType = (typeof REFERRAL_PLAN_TYPE)[keyof typeof REFERRAL_PLAN_TYPE];

export const REFERRAL_REWARD_STATUS = {
	Pending: 'pending',
	Scheduled: 'scheduled',
	Applied: 'applied',
	Cancelled: 'cancelled',
	Review: 'review'
} as const;
export type ReferralRewardStatus =
	(typeof REFERRAL_REWARD_STATUS)[keyof typeof REFERRAL_REWARD_STATUS];

// Snapshot of the reward active at the time the referrer shared their link.
// display_name is shown directly in UI (e.g. "50€ en tu plan") — keep it short.
// display_description is shown in the invite modal.
export interface RewardSnapshotI {
	type: string;
	display_name: string;
	display_description: string;
	amount_numeric: number | null;
	amount_period_months: number | null;
	snapshot_taken_at: string;
}

// Returned by POST /referrals/codes — creates or retrieves the user's referral code.
export interface ReferralCodeI {
	code: string;
	url: string;
	reward_snapshot: RewardSnapshotI;
}

export interface ReferralAttributionI {
	id: string;
	// null when the referred user hasn't provided their name yet (e.g. incomplete registration)
	referred_first_name: string | null;
	// single character + period (e.g. "G."), null for same reason as above
	referred_initial_surname: string | null;
	status: ReferralAttributionStatus;
	// Allows differentiating transfer vs new plan if needed in the future.
	referred_plan_type: ReferralPlanType;
	eligible_for_reward: boolean;
	registered_at: string;
	// null while status === 'registered'
	converted_at: string | null;
}

export interface ReferralRewardI {
	id: string;
	// FK to ReferralAttributionI.id — required to match reward to referral without relying on name.
	attribution_id: string;
	reward_type: string;
	amount_numeric: number | null;
	amount_period_months: number | null;
	status: ReferralRewardStatus;
	// ISO date string (date only, no time) of when the reward will be applied.
	scheduled_for: string | null;
	applied_at: string | null;
	// For display only — do not use for matching logic (use attribution_id instead).
	from_referral_first_name: string | null;
}

export interface ReferralStatsI {
	total_invited: number;
	registered: number;
	converted_transfer: number;
	converted_new_plan: number;
	rewards_pending: number;
	rewards_applied: number;
}

// Response from GET /referrals/me — extends ReferralCodeI to return everything in a single call.
export interface MyReferralsResponseI extends ReferralCodeI {
	attributions: ReferralAttributionI[];
	rewards: ReferralRewardI[];
	stats: ReferralStatsI;
}
